import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { get, includes, indexOf, isEmpty } from "lodash";
import { Spinner } from "@px/px_design_system/lib/components/Spinner";
import { getEmbedUrl } from "../../../../utils/embedUtils";
import { getErrorMessage } from "../../../../utils";
import { UserContext } from "../../context/auth";

import "./style.css";

const DEFAULT_DASHBOARD_IDS = [
  "revenue_summary",
  "revenue_daily",
  "revenue_breakout",
  "health_breakout",
  "deal_monitoring",
  "campaign_troubleshooting",
  "placement_delivery",
];

const getEmbedURL =
  (
    setIframeLoaded,
    setEmbedUrl,
    userData,
    orgData,
    isInvalidSuperUser,
    props,
    modelType = "pub_views"
  ) =>
  (id) => {
    const isDirectSoldPublisher = get(
      orgData,
      "ext.direct_sold_enabled",
      false
    );

    /* istanbul ignore next */
    if (isDirectSoldPublisher && id === "revenue_summary") {
      id = "revenue_summary_direct_sold";
    }

    setIframeLoaded(false);
    setEmbedUrl("");

    const dashboard_url = `/embed/dashboards-next/${modelType}::${id}?embed_domain=${window.location.origin}&Currency=${orgData.config.allowed_currency}`;

    const additionalUserAttributes = props.placement_id
      ? { delivery_overview_placement_id: props.placement_id }
      : {};

    // Use the correct looker model based on the modelType
    const lookerModels =
      modelType === "pub_planner" ? ["pub_planner"] : ["pub_views"];

    return getEmbedUrl({
      userData,
      orgData,
      isInvalidSuperUser,
      embedPath: dashboard_url,
      errorMessageKey: "delivery overview",
      additionalUserAttributes,
      lookerModels,
    })
      .then((url) => {
        if (url) {
          setEmbedUrl(url);
        }
      })
      .catch((error) => {
        if (!error.data) {
          return Promise.reject(error);
        }
        return Promise.reject(getErrorMessage("delivery overview"));
      });
  };

const EmbedIframe = ({
  modelType = "pub_views",
  dashboardIds = DEFAULT_DASHBOARD_IDS,
  ...props
}) => {
  const { location, history } = props;
  const { data, loading, isInvalidSuperUser } = useContext(UserContext);
  const { orgData, userData } = data;
  const [embedUrl, setEmbedUrl] = useState("");
  const [activeTab, setAciveTab] = useState();
  const [isIframeLoaded, setIframeLoaded] = useState(false);
  const getDashboard = getEmbedURL(
    setIframeLoaded,
    setEmbedUrl,
    userData,
    orgData,
    isInvalidSuperUser,
    props,
    modelType
  );
  const spinnerProps = {
    className: "spinner-icon",
    color: "primary",
    loadingtext: "Loading",
  };

  useEffect(() => {
    if (isEmpty(orgData) || loading) {
      return;
    }

    const { params } = props.match;
    const prepareID = getDashboardId(params, props.reportId, dashboardIds);

    if (!prepareID) {
      redirectToDefault(orgData.id, location, history);
      return;
    }

    setAciveTab(indexOf(dashboardIds, prepareID));
    getDashboard(prepareID).catch(() => {});
  }, [loading, location.pathname]);

  const getDashboardId = (params, reportId, dashboardIds) => {
    if (reportId) {
      return reportId;
    }

    if (params.reportingStatus) {
      const statusId = `campaign_${params.reportingStatus}`;
      return includes(dashboardIds, statusId) ? statusId : null;
    }

    if (params.reportingType && params.reportingSubType) {
      const typeId = `${params.reportingType}_${params.reportingSubType}`;
      return includes(dashboardIds, typeId) ? typeId : null;
    }

    return null;
  };

  const redirectToDefault = (orgId, location, history) => {
    const targetPath = `/orgs/${orgId}/reporting/revenue/summary`;
    if (location.pathname !== targetPath) {
      history.replace(targetPath);
    }
  };

  return (
    <div className="embed--reporting__wrapper">
      {!isIframeLoaded && (
        <Spinner
          {...spinnerProps}
          spinnerposition="container"
          data-testid="spinner-loading"
        />
      )}
      {!isEmpty(embedUrl) && (
        <iframe
          id="embed--reporting"
          className={`${dashboardIds[+activeTab]}--reporting ${
            !isIframeLoaded && "hide"
          }`}
          src={`${embedUrl}`}
          title="reporting"
          onLoad={() => setIframeLoaded(true)}
          data-testid="iframe-reporting"
        />
      )}
    </div>
  );
};

EmbedIframe.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  match: PropTypes.shape({
    params: PropTypes.shape({
      reportingType: PropTypes.string,
      reportingSubType: PropTypes.string,
      reportingStatus: PropTypes.string,
    }),
  }),
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
  modelType: PropTypes.string,
  dashboardIds: PropTypes.arrayOf(PropTypes.string),
};

export default EmbedIframe;
